import { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

export type UserRole = "client" | "therapist" | undefined;

export const useRole = () => {
  const { profile } = useAuth();
  const { t } = useTranslation();

  const role: UserRole = profile?.role as UserRole;

  const isTherapist = role === "therapist";
  const isClient = role === "client";

  const label = useMemo(() => {
    if (!role) return null;
    return t(`roles.${role}`, role);
  }, [role, t]);

  return { role, label, isTherapist, isClient };
};

export const useIsTherapist = () => {
  const { isTherapist } = useRole();
  return isTherapist;
};

export const useIsClient = () => {
  const { isClient } = useRole();
  return isClient;
};

export const useRequireTherapist = (redirectTo = "/dashboard") => {
  const { isTherapist } = useRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window !== "undefined" && !isTherapist) {
      navigate(redirectTo, { replace: true });
    }
  }, [isTherapist, navigate, redirectTo]);
};

export const useRequireClient = (redirectTo = "/dashboard/client/journey") => {
  const { isClient } = useRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window !== "undefined" && !isClient) {
      navigate(redirectTo, { replace: true });
    }
  }, [isClient, navigate, redirectTo]);
};

export default useRole;
